import { Camera, FolderKanban, GitCompare, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useAppContext } from "@/context/AppContext";
import { useProfilesContext } from "@/context/ProfileContext";
import { logAction } from "@/db/audit";
import { markMigrated, migrate, needsMigration } from "@/db/migrations/v01ToV02";
import { FLAG_ONBOARDED, setFlag } from "@/lib/storage/flags";

const HIGHLIGHTS: { icon: typeof FolderKanban; title: string; text: string }[] = [
  { icon: FolderKanban, title: "Profiles", text: "Remember each catalog’s context across uploads." },
  { icon: Camera, title: "Snapshots", text: "Every processed catalog is saved locally over time." },
  { icon: GitCompare, title: "Comparison", text: "See exactly what changed between snapshots." },
  { icon: ShieldCheck, title: "Privacy Inspector", text: "Prove nothing leaves your browser." },
];

/**
 * First-run experience for users upgrading from v0.1 (detected via localStorage).
 * Offers to migrate settings and optionally save the current in-memory session
 * as the first profile. Logs the outcome to the audit log.
 */
export function MigrationDialog(): JSX.Element | null {
  const [open, setOpen] = useState(() => needsMigration());
  const { setConfig, result, source } = useAppContext();
  const { setActiveProfile, refresh } = useProfilesContext();
  const [saveSession, setSaveSession] = useState(true);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const hasSession = result !== null && result.rows.length > 0;
  const sessionLabel = source?.fileName ? source.fileName.replace(/\.csv$/i, "") : "My Catalog";

  async function runMigrate(): Promise<void> {
    setBusy(true);
    try {
      const outcome = await migrate({
        saveSessionAsProfile: saveSession && hasSession,
        profileLabel: sessionLabel,
        sessionRows: hasSession ? result?.rows : undefined,
        sessionFilename: source?.fileName,
      });
      setConfig(outcome.migratedConfig);
      if (outcome.createdProfileId !== null) {
        setActiveProfile(outcome.createdProfileId);
        await refresh();
      }
      await logAction({
        category: "config_change",
        action: "Migrated from CatalogKit v0.1",
        actor: "user",
        details: {
          created_profile: outcome.createdProfileId,
          created_snapshot: outcome.createdSnapshotId,
        },
      });
      setFlag(FLAG_ONBOARDED);
      toast.success("Welcome to CatalogKit v0.2");
      setOpen(false);
    } catch (caught) {
      toast.error("Migration failed", {
        description: caught instanceof Error ? caught.message : String(caught),
      });
    } finally {
      setBusy(false);
    }
  }

  async function startFresh(): Promise<void> {
    markMigrated();
    setFlag(FLAG_ONBOARDED);
    await logAction({
      category: "config_change",
      action: "Skipped v0.1 migration (start fresh)",
      actor: "user",
      details: {},
    });
    setOpen(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Require an explicit choice; ignore outside-click close.
        if (!next) return;
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Welcome to CatalogKit v0.2</DialogTitle>
          <DialogDescription>
            Your v0.1 settings are still here. v0.2 adds catalog memory and comparison — all still
            100% local, with zero telemetry.
          </DialogDescription>
        </DialogHeader>

        <ul className="grid gap-3 sm:grid-cols-2">
          {HIGHLIGHTS.map(({ icon: Icon, title, text }) => (
            <li key={title} className="flex gap-2">
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-medium">{title}</p>
                <p className="text-xs text-muted-foreground">{text}</p>
              </div>
            </li>
          ))}
        </ul>

        {hasSession ? (
          <label className="flex items-center gap-2 rounded-md border p-3 text-sm">
            <input
              type="checkbox"
              checked={saveSession}
              onChange={(event) => setSaveSession(event.target.checked)}
              className="h-4 w-4"
            />
            <Label className="cursor-pointer font-normal">
              Save my current session as my first profile ({sessionLabel})
            </Label>
          </label>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={() => void startFresh()} disabled={busy}>
            Start fresh
          </Button>
          <Button onClick={() => void runMigrate()} disabled={busy}>
            {busy ? "Migrating…" : "Migrate my settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
